import {
  APPOINTMENT_CHECKOUT_STATUSES,
  AutomationStatus,
  IMarketingCampaignEntry,
  IPractice,
  ITaskPayloadV2,
  ITemplateScopeData,
  TemplateScope,
  TimingDirection,
  type IAppointment,
  type IAutomation,
  type IAutomationResource,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  Timestamp,
  toMoment,
  type WithRef,
} from '@principle-theorem/shared';
import { Automation } from './automation';
import {
  ConditionHandler,
  getScopedConditions,
  resolveConditionImplementations,
} from '../../condition-logic/condition-handler';
import { ScopeDataBuilder } from '../../templating/scope-data-builder';
import { OrganisationCache } from '../organisation/organisation-cache';

export class AutomationOperators {
  static exceedsRetryLimit<T extends IAutomationResource>(
    automation: Pick<IAutomation<T>, 'cloudTask'>,
    retryLimit: number
  ): boolean {
    return (
      !!automation.cloudTask && automation.cloudTask.dispatchCount >= retryLimit
    );
  }

  static hasTriggerDateChange<T extends IAutomationResource>(
    before: WithRef<IAutomation<T>>,
    after: WithRef<IAutomation<T>>
  ): boolean {
    return this.hasDateChanged(before.triggerDate, after.triggerDate);
  }

  static hasTriggerAfterDateChange<T extends IAutomationResource>(
    before: WithRef<IAutomation<T>>,
    after: WithRef<IAutomation<T>>
  ): boolean {
    return this.hasDateChanged(before.triggerAfterDate, after.triggerAfterDate);
  }

  static hasDateChanged(before?: Timestamp, after?: Timestamp): boolean {
    if (!before && !after) {
      return false;
    }
    if ((!before && after) || (before && !after)) {
      return true;
    }
    return before && after ? !toMoment(before).isSame(toMoment(after)) : false;
  }

  static changedStatusToPending<T extends IAutomationResource>(
    before: WithRef<IAutomation<T>>,
    after: WithRef<IAutomation<T>>
  ): boolean {
    return (
      before.status !== after.status &&
      after.status === AutomationStatus.Pending
    );
  }

  static changedStatusToCancelled<T extends IAutomationResource>(
    before: WithRef<IAutomation<T>>,
    after: WithRef<IAutomation<T>>
  ): boolean {
    return (
      before.status !== after.status &&
      after.status === AutomationStatus.Cancelled
    );
  }

  static changedStatusToDisabled<T extends IAutomationResource>(
    before: WithRef<IAutomation<T>>,
    after: WithRef<IAutomation<T>>
  ): boolean {
    return (
      before.status !== after.status &&
      after.status === AutomationStatus.Disabled
    );
  }

  static appointmentPreconditionMet<T extends IAutomationResource>(
    automation: IAutomation<T>,
    appointment: IAppointment
  ): boolean {
    if (automation.data.timing.direction !== TimingDirection.After) {
      return true;
    }

    return APPOINTMENT_CHECKOUT_STATUSES.includes(appointment.status);
  }

  static async meetsRequiredConditions<
    T extends IAutomationResource,
    ScopeData extends ITemplateScopeData,
  >(automation: IAutomation<T>, scopeData: ScopeData): Promise<boolean> {
    if (!automation.data.requiredConditions) {
      return true;
    }

    const scope = Automation.determineScope(automation);
    const handler = new ConditionHandler(
      resolveConditionImplementations(getScopedConditions(scope))
    );

    return handler.passes(automation.data.requiredConditions, scopeData);
  }

  static async getScopeData(
    practiceRef: DocumentReference<IPractice>,
    payload: ITaskPayloadV2,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<ITemplateScopeData> {
    switch (payload.scope) {
      case TemplateScope.Patient:
        const patient = await OrganisationCache.patients.getDoc(
          payload.patientRef
        );
        return ScopeDataBuilder.buildPatientScopeData(
          patient,
          { ref: payload.practiceRef },
          appUrl,
          marketingCampaignEntry
        );
      case TemplateScope.Appointment:
        const appointment = await Firestore.getDoc(payload.appointmentRef);
        return ScopeDataBuilder.buildAppointmentScopeData(
          appointment,
          appUrl,
          marketingCampaignEntry
        );
      case TemplateScope.TreatmentPlan:
        const treatmentPlan = await Firestore.getDoc(payload.treatmentPlanRef);
        return ScopeDataBuilder.buildTreatmentPlanScopeData(
          treatmentPlan,
          await Firestore.getDoc(payload.practiceRef),
          appUrl,
          marketingCampaignEntry
        );
      case TemplateScope.TreatmentStep:
        const treatmentStep = await Firestore.getDoc(payload.treatmentStepRef);
        return ScopeDataBuilder.buildTreatmentStepScopeData(
          treatmentStep,
          await Firestore.getDoc(payload.practiceRef),
          appUrl,
          marketingCampaignEntry
        );
      case TemplateScope.Invoice:
        const invoice = await Firestore.getDoc(payload.invoiceRef);
        return ScopeDataBuilder.buildInvoiceScopeData(
          invoice,
          appUrl,
          marketingCampaignEntry
        );
      default:
        return {
          practice: await Firestore.getDoc(practiceRef),
        };
    }
  }
}
